import { Column } from "@tento-ui/component-builtin";
import { Storybook } from "@tento-ui/ui/storybook";
import { Toast } from "@tento-ui/components/toast/toast";
import { BrowserChrome } from "@tento-ui/components/browser-chrome";
import { Transition } from "@tento-ui/components/transition";
import { PromoConfirmationView } from "@tento-ui/brand/plan-selector/promo-confirmation-view";
import { PlanSelector } from "@tento-ui/brand/plan-selector/plan-selector";

const MOCK_PLANS: PlanSelector.Plan[] = [
  {
    id: "Trial",
    title: "Contact Us",
    caption: "Need a custom rollout or workflow? We can help",
    tier: 0,
    features: ["Calendar-led workflow design", "Workspace rollout support", "Dedicated implementation guidance"],
  },
  {
    id: "Pro",
    title: "Professional",
    caption: "Best for individuals who want a clearer week",
    tag: "Most popular",
    tier: 1,
    priceUsdPerMonth: 9.99,
    priceUsdPerMonthAnnual: 8.49,
    features: [
      "Calendar, tasks, and notes in one weekly view",
      "Stickers for travel, recovery, focus, and deadlines",
      "Daily planning that starts from real events",
      "Shareable availability links",
    ],
  },
  {
    id: "Teams",
    title: "Teams",
    caption:
      "Best for teams coordinating time, handoffs, and shared plans",
    tier: 2,
    priceUsdPerMonth: 19.99,
    priceUsdPerMonthAnnual: 16.99,
    features: [
      "Shared planning surfaces for calendars and sheets",
      "Team availability and scheduling workflows",
    ],
  },
];

function makeMockSelector(opts?: { activeProduct?: string; isPromoProduct?: boolean }): PlanSelector {
  return PlanSelector.makeWith({
    plans: MOCK_PLANS,
    activeProduct: opts?.activeProduct,
    isPromoProduct: opts?.isPromoProduct,
    onPlanSelect: (plan, isYearly) => {
      Toast.info(`Selected: ${plan.id}, yearly: ${isYearly}`);
    },
    onRedeemKey: (code) => {
      Toast.info(`Redeeming key: ${code}`);
    },
    onContactUs: () => {
      Toast.info("Contact us clicked");
    },
  });
}

export class PlanOptionsViewStoryBook extends Storybook.Default {
  title(): string {
    return "Plan Options View";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "No Promo (bare selector)",
        render: () => {
          const $selector = makeMockSelector();
          $selector.style.flex = "1";

          return BrowserChrome.makeExample(
            $selector,
            "calendar.lona.so/settings",
            1200,
          ).assignStyles({
            alignSelf: "stretch",
            width: "100%",
          });
        },
      },
      {
        title: "With Promo (showing confirmation)",
        render: () => {
          const $transition = Transition.make();

          const $promoView = PromoConfirmationView.makeWith({
            promoCode: "FRIEND2024",
            grantedPlan: "Professional",
            description: "You've unlocked Professional access with a 30-day free trial. Start planning around the week you already have, with visual markers and shareable availability.",
            onChooseAnother: () => {
              $transition.bindState("after");
              Toast.info("State → selector");
            },
          });

          const $selector = makeMockSelector({ activeProduct: "Pro", isPromoProduct: true });
          $selector.style.flex = "1";

          const $selectorCol = Column.make();
          $selectorCol.assignStyles({ flex: "1", flexGrow: "1" });
          $selectorCol.appendChild($selector);

          $transition.bind({
            state: "before",
            $before: $promoView,
            $after: $selectorCol,
          });
          $transition.setAttribute("height", "fill");

          return BrowserChrome.makeExample(
            $transition,
            "calendar.lona.so/onboarding?key=FRIEND2024",
            1200,
          ).assignStyles({
            alignSelf: "stretch",
            width: "100%",
          });
        },
      },
      {
        title: "With Promo (showing selector, toggle back)",
        render: () => {
          const $transition = Transition.make();

          const $promoView = PromoConfirmationView.makeWith({
            promoCode: "TEAM_PROMO",
            grantedPlan: "Teams",
            description: "You've unlocked Teams access with a 90-day free trial. Coordinate shared calendars, handoffs, and planning sheets around real availability.",
            onChooseAnother: () => {
              $transition.bindState("after");
              Toast.info("State → selector");
            },
          });

          const $selector = makeMockSelector({ activeProduct: "Teams", isPromoProduct: true });
          $selector.style.flex = "1";

          const $selectorCol = Column.make();
          $selectorCol.assignStyles({ flex: "1", flexGrow: "1" });
          $selectorCol.appendChild($selector);

          $transition.bind({
            state: "after",
            $before: $promoView,
            $after: $selectorCol,
          });
          $transition.setAttribute("height", "fill");

          return BrowserChrome.makeExample(
            $transition,
            "calendar.lona.so/settings",
            1200,
          ).assignStyles({
            alignSelf: "stretch",
            width: "100%",
          });
        },
      },
    ];
  }
}
